import { AcornPlaceholder, TYPE_LABEL, absMediaUrl } from "@/components/AcornPlaceholder";
import { Wine, GlassWater } from "lucide-react";

// FIX 8.9h · badge categoria-aware. Quando `wine.categoria !== 'vino'` la card
// mostra la label della categoria (DISTILLATO/LIQUORE/BIRRA/ALTRO) invece
// della tipologia enologica. Previene qualsiasi label vino di default (es.
// "Rosso") su record non-vino, anche se `type` fosse valorizzato per errore.
const CATEGORIA_LABEL = {
    distillato: "Distillato",
    liquore: "Liquore",
    birra: "Birra",
    altro: "Altro",
};

function badgeLabel(wine) {
    const cat = wine?.categoria || "vino";
    if (cat !== "vino") return CATEGORIA_LABEL[cat] || cat.toUpperCase();
    return wine?.type ? TYPE_LABEL[wine.type] : "";
}

/**
 * WineCard.
 *
 * Fase 6.8:
 * - Il listino pubblico è visibile a tutti (anonimi inclusi). La prop
 *   `canSeePrices` viene mantenuta per retrocompatibilità ma ora indica
 *   solo "l'utente è autenticato & ha ruolo con prezzo".
 * - Il prezzo minimo è calcolato dalle formats.price (già pubblico per
 *   anonimi grazie al nuovo serializer). Le formats a 0 restano soppresse.
 * - Card mobile più compatta (padding/thumb ridotti) per aumentare la
 *   densità informativa in mobilità.
 */
export default function WineCard({ wine, onOpen, size = "default", showStock = true }) {
    const prices = (wine.formats || [])
        .map((f) => (typeof f.price === "number" ? f.price : null))
        .filter((p) => p !== null && p > 0);
    const hasPrices = prices.length > 0;
    const minP = hasPrices ? Math.min(...prices) : null;

    const thumbUrl = absMediaUrl(wine.images?.thumb_url);
    const cardUrl = absMediaUrl(wine.images?.card_url);
    const displayUrl = size === "large" ? (cardUrl || thumbUrl) : (thumbUrl || cardUrl);

    // FIX 8.9j-P1 · giacenze nascondibili via toggle staff (default: mostra).
    // Il campo `stock_quantity` è già filtrato lato serializer per i non
    // staff/admin, qui gestiamo solo la resa visiva.
    const stockRow = showStock && wine.formats.some((f) => typeof f.stock_quantity === "number")
        ? wine.formats.map((f) => `${f.size} L · ${f.stock_quantity ?? "—"}`).join(" · ")
        : null;

    // Variante `compact` (Fase 7.2): densità massima. 3 colonne mobile,
    // thumb piccolissima, SOLO nome + prezzo (produttore/annata nascosti).
    if (size === "compact") {
        return (
            <button
                onClick={onOpen}
                data-testid={`wine-card-${wine.id}`}
                className="group co-card text-left p-1.5 sm:p-2 flex flex-col gap-1 hover:border-[hsl(var(--primary))] transition-colors w-full h-full"
            >
                <div className="w-full flex items-center justify-center bg-[hsl(var(--secondary))] rounded-sm h-14 sm:h-20 overflow-hidden">
                    {displayUrl ? (
                        <img
                            src={displayUrl}
                            alt={wine.name}
                            loading="lazy"
                            className="max-h-full max-w-full object-contain"
                        />
                    ) : (
                        <AcornPlaceholder size={30} opacity={0.5} />
                    )}
                </div>
                <div className="flex-1 flex flex-col min-w-0">
                    <h3 className="text-[11px] sm:text-[13px] leading-tight text-[hsl(var(--foreground))] line-clamp-2">
                        {wine.name}
                    </h3>
                    <div className={`mt-auto pt-0.5 co-price text-[11px] sm:text-xs ${!hasPrices ? "text-[hsl(var(--muted-foreground))]" : ""}`}>
                        {hasPrices ? `€ ${minP.toFixed(2)}` : "—"}
                    </div>
                </div>
            </button>
        );
    }

    if (size === "large") {
        return (
            <button
                onClick={onOpen}
                data-testid={`wine-card-${wine.id}`}
                className="group co-card text-left p-6 sm:p-7 flex flex-col gap-4 hover:border-[hsl(var(--primary))] transition-[border-color,transform] duration-300 hover:-translate-y-0.5 w-full h-full"
            >
                <div className="w-full flex items-center justify-center bg-[hsl(var(--secondary))] rounded-sm h-52 sm:h-64 overflow-hidden">
                    {displayUrl ? (
                        <img
                            src={displayUrl}
                            alt={wine.name}
                            loading="lazy"
                            className="max-h-full max-w-full object-contain"
                        />
                    ) : (
                        <AcornPlaceholder size={140} opacity={0.55} />
                    )}
                </div>
                <div className="flex-1 flex flex-col">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="co-caption">{badgeLabel(wine)}</span>
                        {wine.glass_available && (
                            <span className="co-badge-calice">
                                <GlassWater className="w-3 h-3" strokeWidth={1.5} /> Al Calice
                            </span>
                        )}
                    </div>
                    {/* FIX 8.9j-UI · gerarchia tipografica invertita: producer dominante */}
                    <h3 className="text-sm leading-tight text-[hsl(var(--foreground))] mb-1">
                        {wine.name}
                        {wine.vintage && (
                            <span className="ml-2 text-[hsl(var(--muted-foreground))] italic">{wine.vintage}</span>
                        )}
                    </h3>
                    <p className="text-2xl sm:text-3xl leading-tight text-[hsl(var(--muted-foreground))] mb-2">{wine.producer}</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">
                        {wine.region} · {wine.country}
                    </p>
                    <div className="mt-auto pt-5 flex items-end justify-between gap-3">
                        <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                            <Wine className="w-3.5 h-3.5" strokeWidth={1.5} />
                            <span>{wine.formats.map((f) => `${f.size} L`).join(" · ") || "—"}</span>
                        </div>
                        <div className="text-right">
                            <div className="co-caption mb-0.5">
                                {hasPrices && prices.length > 1 ? "Da" : ""}
                            </div>
                            <div className={`co-price text-2xl ${!hasPrices ? "text-[hsl(var(--muted-foreground))]" : ""}`}>
                                {hasPrices ? `€ ${minP.toFixed(2)}` : "—"}
                            </div>
                        </div>
                    </div>
                </div>
            </button>
        );
    }

    // Card default (lista): mobile-compact.
    return (
        <button
            onClick={onOpen}
            data-testid={`wine-card-${wine.id}`}
            className="group co-card text-left p-3 sm:p-6 flex gap-3 sm:gap-6 hover:border-[hsl(var(--primary))] transition-[border-color,transform] duration-300 hover:-translate-y-0.5 w-full"
        >
            <div className="shrink-0 flex items-start pt-1 w-12 sm:w-20">
                {displayUrl ? (
                    <img
                        src={displayUrl}
                        alt={wine.name}
                        loading="lazy"
                        className="max-h-24 sm:max-h-32 max-w-full object-contain"
                    />
                ) : (
                    <AcornPlaceholder size={48} opacity={0.55} className="sm:!size-[72px]" />
                )}
            </div>

            <div className="min-w-0 flex-1 flex flex-col">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="co-caption">{badgeLabel(wine)}</span>
                    {wine.glass_available && (
                        <span className="co-badge-calice" data-testid="wine-card-calice-badge">
                            <GlassWater className="w-3 h-3" strokeWidth={1.5} /> Al Calice
                        </span>
                    )}
                    {wine.status === "da_arricchire" && (
                        <span className="co-badge-neutral">Scheda in preparazione</span>
                    )}
                </div>

                {/* FIX 8.9j-UI · gerarchia tipografica invertita: producer dominante */}
                <h3 className="text-xs sm:text-sm leading-tight text-[hsl(var(--foreground))] mb-0.5 sm:mb-1">
                    {wine.name}
                    {wine.vintage && (
                        <span className="ml-2 text-[hsl(var(--muted-foreground))] italic">
                            {wine.vintage}
                        </span>
                    )}
                </h3>

                <p className="text-lg sm:text-2xl leading-tight text-[hsl(var(--muted-foreground))] mb-1 sm:mb-2 truncate">
                    {wine.producer}
                </p>

                <p className="text-[10px] sm:text-xs text-[hsl(var(--muted-foreground))]">
                    {wine.region} · {wine.country}
                    {wine.denomination ? ` · ${wine.denomination}` : ""}
                </p>

                <div className="mt-auto pt-2 sm:pt-4 flex items-end justify-between gap-3">
                    <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-[hsl(var(--muted-foreground))]">
                        <Wine className="w-3 h-3 sm:w-3.5 sm:h-3.5" strokeWidth={1.5} />
                        <span>{wine.formats.map((f) => `${f.size} L`).join(" · ") || "—"}</span>
                    </div>

                    <div className="text-right">
                        <div className="co-caption mb-0.5 hidden sm:block">
                            {hasPrices && prices.length > 1 ? "Da" : ""}
                        </div>
                        <div
                            className={`co-price ${!hasPrices ? "text-[hsl(var(--muted-foreground))]" : ""}`}
                            data-testid={`wine-card-price-${wine.id}`}
                        >
                            {hasPrices ? `€ ${minP.toFixed(2)}` : "—"}
                        </div>
                    </div>
                </div>

                {stockRow && (
                    <div className="mt-2 text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]" data-testid={`wine-card-stock-${wine.id}`}>
                        Giacenza: {stockRow}
                    </div>
                )}
            </div>
        </button>
    );
}
